import { isNil, omitBy } from 'lodash'

import { api } from 'data/api/api'
import { ApiResponse } from '@marketplace-web/core-api/api-client-util'

import { GetInboxConversationsArgs, InboxConversationsResp } from '../types/get-inbox-conversations'
import { GetInboxListItemArgs, InboxListItemResp } from '../types/get-inbox-list-item'
import {
  TranslateConversationArgs,
  TranslateConversationResp,
} from '../types/translate-conversation'
import { DeleteMessageNewArgs, DeletePhotoMessageNewArgs } from '../types/delete-message'
import {
  PostReplyArgs,
  PostReplyNewError,
  PostReplyNewArgs,
  PostReplyNewResp,
} from '../types/post-reply'
import { ConversationResp } from '../types/conversation'
import { SetConversationAsReadArgs } from '../types/set-conversation-as-read'
import {
  CreateItemConversationArgs,
  CreateItemConversationResp,
} from '../types/create-item-conversation'
import { CreateInquiryArgs, CreateInquiryResp } from '../types/create-inquiry'
import { ConversationNewResp, InboxConversationsRespNew } from '../types/conversation-new'

export const getInboxConversations = ({ currentPage, perPage }: GetInboxConversationsArgs) =>
  api.get<InboxConversationsResp>('/inbox', {
    params: omitBy(
      {
        page: currentPage,
        per_page: perPage,
      },
      isNil,
    ),
  })

export const getInboxListItem = ({ conversationId }: GetInboxListItemArgs) =>
  api.get<InboxListItemResp>(`/inbox/${conversationId}`)

export const translateConversation = ({ conversationId, translate }: TranslateConversationArgs) =>
  api.put<TranslateConversationResp>(`/conversations/${conversationId}/translation`, {
    translate,
  })

export const deleteConversationSuspicion = (threadId: number | string) =>
  api.delete(`/msg_threads/${threadId}/suspicion`)

export const deleteConversation = (id: number | string) => api.delete(`/conversations/${id}`)

export const deleteMessage = ({ conversationId, messageId }: DeleteMessageNewArgs) =>
  api.delete(`conversations/${conversationId}/replies/${messageId}`)

export const deletePhotoMessage = ({
  conversationId,
  messageId,
  photoId,
}: DeletePhotoMessageNewArgs) =>
  api.delete(`/conversations/${conversationId}/replies/${messageId}/photos/${photoId}`)

export const deletePhotoSuspicion = (photoId: number) =>
  api.delete(`/user_msg_photos/${photoId}/suspicion`)

export const postReply = ({
  conversationId,
  body,
  photoTempUuids,
  isPersonalDataSharingCheckSkipped,
}: PostReplyArgs) =>
  api.post<ConversationResp, PostReplyNewError>(`conversations/${conversationId}/replies`, {
    reply: {
      body,
      photo_temp_uuids: photoTempUuids,
      is_personal_data_sharing_check_skipped: isPersonalDataSharingCheckSkipped,
    },
  })

export const postReplyNew = ({
  conversationId,
  content,
  isPersonalDataSharingCheckSkipped,
  photoTempUuids,
}: PostReplyNewArgs) =>
  api.post<PostReplyNewResp>(`/messaging/conversations/${conversationId}/replies`, {
    content,
    is_personal_data_sharing_check_skipped: isPersonalDataSharingCheckSkipped,
    photo_temp_uuids: photoTempUuids,
  })

export const getConversation = (conversationId: number | string) =>
  api.get<ConversationResp>(`/conversations/${conversationId}`)

export const getConversationNew = (id: string | number) =>
  api.get<ConversationNewResp>(`/messaging/conversations/${id}`)

export const setConversationAsRead = ({ conversationId }: SetConversationAsReadArgs) =>
  api.put<ApiResponse>(`/conversations/${conversationId}/mark_as_read`)

export const setConversationAsReadNew = ({ conversationId }: SetConversationAsReadArgs) =>
  api.post<ApiResponse>(`/messaging/conversations/${conversationId}/mark_as_read`)

export const reserveTransactionItems = (transactionId: number, reserved: boolean) =>
  api.put(`/transactions/${transactionId}/reservation`, { reserved })

export const createItemConversationThread = ({
  itemId,
  initiator,
  receiverId,
}: CreateItemConversationArgs) =>
  api.post<CreateItemConversationResp>('/conversations', {
    initiator,
    item_id: itemId,
    opposite_user_id: receiverId,
  })

export const getInboxConversationsNew = (nextCursor?: string | null) =>
  api.get<InboxConversationsRespNew>('/messaging/inbox', {
    params: omitBy(
      {
        next_cursor: nextCursor,
      },
      isNil,
    ),
  })

export const createInquiryNew = ({ itemIds, receiverId }: CreateInquiryArgs) =>
  api.post<CreateInquiryResp>('/messaging/inquiries', {
    item_ids: itemIds,
    receiver_id: receiverId,
  })
